import { defineStore } from 'pinia'
import { reactive } from 'vue'
import ERROR_TYPES from '@/constants/errorTypes'
import wait from '@/utilities/wait'
import { datadogService } from '@/services/datadog'
import { levelForErrorCause } from '@/services/datadogLogPolicy'

/**
 * エラーメッセージのフェードアウトにかける秒数
 * @description FieldErrorMessage の transition-duration はこの値を style 属性で受け取るため、消去処理の待機時間と表示側のアニメーション時間が同期する
 */
const CLEAR_ANIMATION_DELAY = 0.3

/**
 * エラークリアの対象範囲
 */
export interface ErrorClearScope {
  /**
   * バリデーション区画の識別子
   * @description 指定すると、その区画のエラーと、区画に属さない（サーバ由来の）エラーだけを消す。
   *              他の区画（同一画面内の別フォーム）のエラー表示には影響しない。
   */
  namespace?: string,

  /**
   * 消すフィールド名
   * @description 指定すると、そのフィールドのエラーだけを消す（namespace と併用すると両方に当てはまるものだけ）
   */
  fields?: string[],
}

export const useErrorStore = defineStore('error', () => {
  const context: ErrorContext = reactive({
    items: [],
    animationDelay: CLEAR_ANIMATION_DELAY,
  })

  /**
   * 指定の区画に属する検証エラー（と、区画に属さないサーバ由来の検証エラー）があるかどうか
   * @description 同一画面に複数フォームがある場合、他フォームのエラーで自フォームの送信を止めないために使う。
   *              対象範囲は clearErrors({ namespace }) と同じ。
   * @param namespace 対象の区画（ValidationObserver の namespace）
   */
  const hasFieldErrorsIn = (namespace: string): boolean => {
    return context.items.some(item =>
      item.cause === ERROR_TYPES.VALIDATION_ERROR && (!item.namespace || item.namespace === namespace))
  }

  /**
   * エラーの登録
   * @param cause エラー要因
   * @param item フィールド名とメッセージ
   * @param namespace フロントバリデーション由来の場合、その区画の識別子。サーバ由来など区画に属さない場合は空文字
   */
  const setError = (cause: ErrorType, item: ErrorItemInput, namespace = '') => {
    context.items.push({
      field: item.field,
      messages: item.messages,
      cause,
      namespace,
      isShow: true,
    })

    // Track error in Datadog
    const errorContext = {
      error_type: 'application_error',
      error_cause: cause,
      field: item.field,
      messages: item.messages,
    }

    // Create error for tracking
    const error = new Error(`${cause}: ${item.messages.join(', ')}`)
    datadogService.trackError(error, errorContext, levelForErrorCause(cause))
  }

  const isInScope = (item: ErrorItem, scope: ErrorClearScope): boolean => {
    if (scope.fields && !scope.fields.includes(item.field)) return false
    if (scope.namespace === undefined) return true
    return !item.namespace || item.namespace === scope.namespace
  }

  /**
   * エラークリア処理
   * @param scope 消す対象の範囲。未指定なら全件
   *
   * エラークリア発火時にすぐにメッセージを消去すると、
   * 画面のエラーメッセージエリアがフェードアウトアニメーション中にメッセージが空になってしまい、
   * 表示に違和感が出てしまうので、CSS アニメーションの終了を待ってからエラーメッセージを取り除く。
   */
  const clearErrors = async (scope: ErrorClearScope = {}): Promise<void> => {
    const targets = context.items.filter(item => isInScope(item, scope))
    if (!targets.length) return

    targets.forEach(item => { item.isShow = false })
    await wait(context.animationDelay)

    context.items = context.items.filter(item => !targets.includes(item))
  }

  /**
   * サーバから返却されたエラーで画面のエラー状態を置き換える
   */
  const handleErrors = (error: { cause: ErrorType, items: ErrorItemInput[] }) => {
    // Datadog への送信は axiosClient の response interceptor が担う（同じ失敗を error_type 違いで重ねない）
    context.items = error.items.map(item => ({
      field: item.field,
      messages: item.messages,
      cause: error.cause,
      namespace: '',
      isShow: true,
    }))
  }

  const getErrorItemsByFieldName = (field: string): ErrorItem[] => {
    if (!field) return []
    return context.items.filter(item => item.field === field)
  }

  /**
   * 指定のフィールド名のメッセージを抽出
   * @param field 取得したいエラーメッセージフィールド名
   * @returns エラーメッセージの string 配列
   */
  const getErrorMessagesByFieldName = (field: string): string[] => {
    return getErrorItemsByFieldName(field).flatMap(item => item.messages)
  }

  /**
   * 指定のフィールドのエラーが表示状態かどうか（消去アニメーション中は false）
   * @param field 対象のフィールド名
   */
  const isShowErrorsByFieldName = (field: string): boolean => {
    return getErrorItemsByFieldName(field).some(item => item.isShow)
  }

  return {
    // data
    context,

    // methods
    hasFieldErrorsIn,
    setError,
    clearErrors,
    handleErrors,
    getErrorMessagesByFieldName,
    isShowErrorsByFieldName,
  }
})
